"use client";

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Database } from "supabase/database.types";

import Avatar from "../Avatar";

type CurrentScore = Pick<
  Database["public"]["Tables"]["profile_rank"]["Row"],
  "level" | "level_max_score" | "level_min_score" | "level_title" | "score"
>;

// Custom Score Notification Component
interface ScoreNotificationProps {
  message?: string;
  newScore: CurrentScore;
  pointsEarned: number;
  previousScore: CurrentScore;
  visible: boolean;
}

export const showScoreIncreaseNotification = (
  pointsEarned: number,
  newScore: CurrentScore,
  previousScore: CurrentScore
) => {
  toast.custom(
    (t) => (
      <ScoreNotification
        newScore={newScore}
        pointsEarned={pointsEarned}
        previousScore={previousScore}
        visible={t.visible}
      />
    ),
    { duration: 4000, id: `score-${newScore.score}` }
  );
};

const getProgress = (score: CurrentScore) =>
  Math.round(
    ((score.score - score.level_min_score) /
      (score.level_max_score - score.level_min_score)) *
      100
  );

export default function ScoreNotification({
  newScore,
  pointsEarned,
  previousScore,
  visible,
}: ScoreNotificationProps) {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [displayScore, setDisplayedScore] = useState(previousScore);
  const [enableTransition, setEnableTransition] = useState(true);

  const leveledUp = newScore.level > previousScore.level;
  const newProgress = getProgress(newScore);
  const previousProgress = getProgress(previousScore);

  useEffect(() => {
    if (leveledUp) {
      // First, animate to 100% for the previous level
      const animateToComplete = () => {
        const duration = 800;
        const steps = 40;
        const stepDuration = duration / steps;
        const progressDiff = 100 - previousProgress;
        const progressPerStep = progressDiff / steps;

        let currentStep = 0;
        const interval = setInterval(() => {
          currentStep++;
          if (currentStep >= steps) {
            setAnimatedProgress(100);
            clearInterval(interval);

            // After reaching 100%, jump to new level
            setTimeout(() => {
              setDisplayedScore(newScore);
              setEnableTransition(false);
              setAnimatedProgress(0);

              // Re-enable transition and animate to new progress
              setTimeout(() => {
                setEnableTransition(true);
                animateToNewProgress();
              }, 50);
            }, 200);
          } else {
            setAnimatedProgress(
              previousProgress + progressPerStep * currentStep
            );
          }
        }, stepDuration);
      };

      // Animate to new progress within the new level
      const animateToNewProgress = () => {
        const duration = 800;
        const steps = 40;
        const stepDuration = duration / steps;
        const progressPerStep = newProgress / steps;

        let currentStep = 0;
        const interval = setInterval(() => {
          currentStep++;
          if (currentStep >= steps) {
            setAnimatedProgress(newProgress);
            clearInterval(interval);
          } else {
            setAnimatedProgress(progressPerStep * currentStep);
          }
        }, stepDuration);
      };

      animateToComplete();
    } else {
      // Same level, just animate to new progress
      const duration = 1200;
      const steps = 60;
      const stepDuration = duration / steps;
      const progressDiff = newProgress - previousProgress;
      const progressPerStep = progressDiff / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        if (currentStep >= steps) {
          setAnimatedProgress(newProgress);
          clearInterval(interval);
        } else {
          setAnimatedProgress(previousProgress + progressPerStep * currentStep);
        }
      }, stepDuration);
    }
  }, [leveledUp, newProgress, previousProgress, newScore]);

  return (
    <div
      className={
        visible ? "animate-notification-enter" : "animate-notification-leave"
      }
    >
      <div
        className={"relative flex items-center gap-3 bg-white dark:bg-slate-800 rounded-full px-4 py-3 shadow-lg min-w-[280px]"}
      >
        {/* Glow effects for Level Up */}
        {displayScore.level > previousScore.level && (
          <div className="absolute -top-2 -left-2 -right-2 -bottom-2 inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 rounded-full animate-pulse" />
        )}

        {/* Avatar with Progress Circle */}
        <div className="relative flex-shrink-0">
          {/* Progress Circle */}
          <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48">
            {/* Background circle */}
            <circle
              className="text-slate-200 dark:text-slate-600"
              cx="24"
              cy="24"
              fill="none"
              r="20"
              stroke="currentColor"
              strokeWidth="3"
            />
            {/* Progress circle */}
            <circle
              className={`text-yellow-600 dark:text-yellow-300 ${
                enableTransition ? "transition-all duration-300 ease-out" : ""
              }`}
              cx="24"
              cy="24"
              fill="none"
              r="20"
              stroke="currentColor"
              strokeDasharray={`${2 * Math.PI * 20}`}
              strokeDashoffset={`${
                2 * Math.PI * 20 * (1 - animatedProgress / 100)
              }`}
              strokeLinecap="round"
              strokeWidth="3"
            />
          </svg>

          <Avatar className="absolute" />
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 dark:text-white truncate">
            {displayScore.level_title}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Level {displayScore.level}
            {pointsEarned > 0 && ` • +${pointsEarned} XP`}
          </div>
        </div>
      </div>
    </div>
  );
}
